/**
 * The tier offer as frontend booleans, read off the one catalog
 * (components/settings/tiers/tierCatalog.ts, which mirrors the backend's
 * app/plans/catalog.py). The plan rides on the auth session, so this needs no
 * extra request. Staff surfaces may show locked features with an upgrade hint;
 * learner surfaces never show locked features at all (product rule).
 */
import { useAuth } from '../contexts/AuthContext';
import {
  FEATURE,
  normalizeTierId,
  planHasFeature,
  planLimit,
} from '../components/settings/tiers/tierCatalog';

export interface PlanFeatures {
  plan: string;
  /** SCORM package modules (Pro+). */
  scorm: boolean;
  /** Completion certificates at all (Growth+ auto-generated template). */
  certificates: boolean;
  /** Custom-designed certificate uploads (Pro+). */
  customCertificates: boolean;
  /** Bulk learner import tools (Growth+). */
  bulkTools: boolean;
  /** Department/role attribute filters (Growth+). */
  attributeFilters: boolean;
  /** Calendar + Google integration (Growth+). */
  calendar: boolean;
  /** Invitation emails to learners (Growth+). */
  invitationEmails: boolean;
  /** Learning programs (Growth+). */
  programs: boolean;
  /** Program analytics (Pro+). */
  programAnalytics: boolean;
}

export function usePlanFeatures(): PlanFeatures {
  const { activeMembership } = useAuth();
  // normalizeTierId resolves legacy ids (pre-v3 'starter' -> 'growth') so a
  // grandfathered workspace gets the features it actually pays for.
  const plan = normalizeTierId(activeMembership?.tenant?.subscription_plan ?? 'free');
  return {
    plan,
    scorm: planHasFeature(FEATURE.scorm, plan),
    certificates: planHasFeature(FEATURE.certificateTemplate, plan),
    customCertificates: planHasFeature(FEATURE.certificateCustom, plan),
    bulkTools: planHasFeature(FEATURE.bulkTools, plan),
    invitationEmails: planHasFeature(FEATURE.invitationEmails, plan),
    attributeFilters: planHasFeature(FEATURE.attributeFilters, plan),
    calendar: planHasFeature(FEATURE.calendar, plan),
    // Programs are gated by their allowance, not a flag — mirrors the backend
    // check in routes/programs.py (a limit of 0 means "not on this plan").
    programs: planLimit('publishedPrograms', plan) !== 0,
    programAnalytics: planHasFeature(FEATURE.programAnalytics, plan),
  };
}
